import { useState, useEffect } from "react";
import axios from "axios";
import PizZip from "pizzip";
import Docxtemplater from "docxtemplater";
import { saveAs } from "file-saver";
import ImageModule from "docxtemplater-image-module-free";
import { PDFDocument } from "pdf-lib";
import EmailForm from "./EmailForm";
import AlertDialog from "./AlertDialog";
import { TrashIcon } from "@heroicons/react/24/outline";
const API = "https://erp-xe9u.onrender.com/api";

export default function ProposalList({
  proposals,
  setProposals,
  setOpportunities,
  opportunityId,
  companyProfile,
  userEmail,
  accounts,
  contacts,
  opportunities,
  setIsEditingProposal,
  fetchProposals,
}) {
  const [pdfBuffer, setPdfBuffer] = useState(null);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [currentProposal, setCurrentProposal] = useState(null);
  const [appendices, setAppendices] = useState([]);
  const [showAwardModal, setShowAwardModal] = useState(false);
  const [awardFormData, setAwardFormData] = useState({
    proposalId: "",
    projectManager: "",
    foreman: "",
  });
  const [users, setUsers] = useState([]);
  const [alert, setAlert] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
    onConfirm: () => {},
    showCancel: false,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        const [appendicesResponse, usersResponse] = await Promise.all([
          axios.get(`${API}/company-profile/opportunity-appendices`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API}/users`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        setAppendices(appendicesResponse.data);
        const internalUsers = usersResponse.data.filter((user) =>
          ["admin", "project_manager", "foreman", "manager"].includes(
            user.role,
          ),
        );
        setUsers(internalUsers);
        console.log(appendicesResponse.data);
      } catch (err) {
        console.error("Error fetching data:", err);
        setAlert({
          open: true,
          type: "error",
          title: "Error",
          message: "Failed to load data. Please try again.",
          onConfirm: () => setAlert({ ...alert, open: false }),
        });
      }
    };

    fetchData();
  }, []);

  const handleAwardProposal = (proposal) => {
    setAwardFormData({
      proposalId: proposal._id,
      projectManager: "",
      foreman: "",
    });
    setShowAwardModal(true);
  };

  const handleAwardSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/opportunities/${opportunityId}/award`,
        awardFormData,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setOpportunities((prev) =>
        prev.map((opp) =>
          opp._id === opportunityId
            ? { ...opp, ...response.data.opportunity }
            : opp,
        ),
      );

      setShowAwardModal(false);
      setAwardFormData({ proposalId: "", projectManager: "", foreman: "" });
      setAlert({
        open: true,
        type: "success",
        title: "Success",
        message: "Opportunity awarded and project created successfully!",
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Failed to award opportunity: " +
          (err.response?.data?.message || err.message),
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
    }
  };

  const handleAwardCancel = () => {
    setShowAwardModal(false);
    setAwardFormData({ proposalId: "", projectManager: "", foreman: "" });
  };

  const handleDeleteProposal = async (proposalId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/proposals/${proposalId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchProposals();
      setOpportunities((prev) =>
        prev.map((opp) =>
          opp._id === opportunityId
            ? {
                ...opp,
                relatedProposals: opp.relatedProposals.filter(
                  (p) => p._id !== proposalId,
                ),
              }
            : opp,
        ),
      );
      setAlert({
        open: true,
        type: "success",
        title: "Success",
        message: "Proposal deleted successfully!",
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Failed to delete proposal: " +
          (err.response?.data?.message || err.message),
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
    }
  };

  const formatCurrency = (value) => {
    const numValue = parseFloat(value) || 0;
    const isNegative = numValue < 0;
    const absoluteValue = Math.abs(numValue);
    const formatted = absoluteValue.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return isNegative ? `-$${formatted}` : `$${formatted}`;
  };

  const generateDocx = async (proposal, sendEmail = false) => {
    try {
      const response = await fetch("/proposal_template.docx");
      if (!response.ok) {
        throw new Error(`Failed to load template: ${response.statusText}`);
      }
      const templateArrayBuffer = await response.arrayBuffer();

      const zip = new PizZip(templateArrayBuffer);

      const base64Regex =
        /^(?:data:)?image\/(png|jpg|jpeg|svg|svg\+xml);base64,/;
      const validBase64 =
        /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;

      function base64Parser(tagValue) {
        if (typeof tagValue !== "string" || !base64Regex.test(tagValue)) {
          return null;
        }
        const stringBase64 = tagValue.replace(base64Regex, "");
        if (!validBase64.test(stringBase64)) {
          throw new Error(
            "Error parsing base64 data, your data contains invalid characters",
          );
        }
        const binaryString = window.atob(stringBase64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes.buffer;
      }

      let imageBase64;
      if (
        companyProfile?.logo &&
        !companyProfile.logo.startsWith("data:image")
      ) {
        const logoResponse = await fetch(companyProfile.logo);
        if (logoResponse.ok) {
          const logoBlob = await logoResponse.blob();
          const reader = new FileReader();
          imageBase64 = await new Promise((resolve) => {
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(logoBlob);
          });
        }
      } else if (companyProfile?.logo) {
        imageBase64 = companyProfile.logo;
      }

      const imageOptions = {
        getImage: (tagValue) => base64Parser(tagValue),
        getSize: () => [125, 90],
      };

      const doc = new Docxtemplater(zip, {
        paragraphLoop: true,
        linebreaks: true,
        modules: [new ImageModule(imageOptions)],
      });

      const sequenceOfWorkItems = proposal.sequenceOfWork
        ? proposal.sequenceOfWork
            .split("\n")
            .map((line, idx) => ({
              text: line.replace(/^\d+\.\s*/, "").trim(),
              index: idx + 1,
            }))
            .filter((item) => item.text.length > 0)
        : [{ text: "N/A", index: 1 }];

      const opportunity = opportunities.find(
        (opp) =>
          (opp._id || opp) ===
          (proposal.opportunity?._id || proposal.opportunity),
      );

      const account = accounts.find(
        (a) => String(a._id) === String(opportunity?.account?._id),
      );
      const contact = contacts.find(
        (c) => String(c._id) === String(opportunity?.contact._id),
      );

      const contactAddress = account?.address
        ? `${account.address.street}, ${account.address.city}, ${account.address.state} ${account.address.postalCode}, ${account.address.country}`
        : contact?.address
          ? `${contact.address.street}, ${contact.address.city}, ${contact.address.state} ${contact.address.zip}, ${contact.address.country}`
          : "N/A";

      const services = proposal.lineItems
        .map((item) => {
          const match = item.title.match(/^\d{2}\s\d{2}\s\d{2}\s(.+)$/);
          return match ? match[1].trim() : item.title;
        })
        .join(", ");

      const subtotal = proposal.lineItems.reduce(
        (sum, item) => sum + (parseFloat(item.price) || 0),
        0,
      );

      const taxRate = parseFloat(companyProfile?.defaultTax?.taxRate) || 0;

      const taxAmount = subtotal * (taxRate / 100);
      const totalWithTax = subtotal + taxAmount;

      const data = {
        companyName: companyProfile?.companyName || "",
        companyAddress: companyProfile?.address
          ? `${companyProfile.address.street}, ${companyProfile.address.city}, ${companyProfile.address.state} ${companyProfile.address.zip}, ${companyProfile.address.country}`
          : "N/A",
        companyPhone: companyProfile?.phone || "N/A",
        sequenceOfWorkItems: sequenceOfWorkItems,
        street: opportunity.jobAddress.street,
        city: opportunity.jobAddress.city,
        state: opportunity.jobAddress.state,
        zip: opportunity.jobAddress.postalCode,
        country: opportunity.jobAddress.country,
        proposalTitle: proposal.title,
        opportunityTitle: opportunity?.title || "N/A",
        accountName: account?.companyName || "N/A",
        accountAddress: account?.address
          ? `${account.address.street}, ${account.address.city}, ${account.address.state} ${account.address.postalCode}, ${account.address.country}`
          : "N/A",
        contactName: contact
          ? `${contact.firstName} ${contact.lastName}`
          : "N/A",
        contactEmail: contact?.email || "N/A",
        contactAddress: contactAddress,
        drawingDate: proposal.drawingDate
          ? (() => {
              const parsedDate = new Date(proposal.drawingDate);
              if (isNaN(parsedDate.getTime())) return "N/A";
              // Extract YYYY-MM-DD and create a new Date object to avoid time zone shift
              const dateOnly = parsedDate.toISOString().split("T")[0];
              const localDate = new Date(dateOnly + "T00:00:00"); // Treat as local time
              return localDate.toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              });
            })()
          : "N/A",
        sequenceOfWork: proposal.sequenceOfWork || "N/A",
        anticipatedStartDate: proposal.anticipatedStartDate
          ? (() => {
              const parsedDate = new Date(proposal.anticipatedStartDate);
              if (isNaN(parsedDate.getTime())) return "N/A";
              // Extract YYYY-MM-DD and create a new Date object to avoid time zone shift
              const dateOnly = parsedDate.toISOString().split("T")[0];
              const localDate = new Date(dateOnly + "T00:00:00"); // Treat as local time
              return localDate.toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              });
            })()
          : "N/A",
        workingDays: proposal.workingDays || 0,
        depositTerms: proposal.depositTerms || "N/A",
        paymentTerms: proposal.paymentTerms || "N/A",
        totalCost: formatCurrency(proposal.totalCost), // Instead of: `$${proposal.totalCost.toLocaleString(...)}`
        totalPrice: formatCurrency(proposal.totalPrice), // Instead of: `$${proposal.totalPrice.toLocaleString(...)}`
        subtotal: formatCurrency(subtotal), // Instead of: `$${subtotal.toLocaleString(...)}`
        taxAmount: formatCurrency(taxAmount), // Instead of: `$${taxAmount.toLocaleString(...)}`
        totalWithTax: formatCurrency(totalWithTax),
        taxRate: `${taxRate.toFixed(2)}%`,
        taxAmount: `$${taxAmount.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,

        lineItems: proposal.lineItems.map((item) => ({
          title: item.title,
          price: formatCurrency(item.price), // Changed from: `$${item.price.toLocaleString(...)}`
          cost: formatCurrency(item.cost),
          markup: `${item.markup}%`,
          scopeOfWork: item.scopeOfWork
            ? `SCOPE OF WORK\n${item.scopeOfWork.replace(
                /^SCOPE OF WORK\n?/,
                "",
              )}`
            : "",
          conditionOfWork: item.conditionOfWork
            ? `CONDITIONS OF WORK\n${item.conditionOfWork.replace(
                /^CONDITIONS OF WORK\n?/,
                "",
              )}`
            : "",
        })),
        services: services || "N/A",
        image: imageBase64,
        createdAt: proposal.createdAt
          ? new Date(proposal.createdAt).toLocaleDateString()
          : "N/A",
      };

      doc.setData(data);
      doc.render();

      const docxBlob = doc.getZip().generate({
        type: "blob",
        mimeType:
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });

      const formData = new FormData();
      formData.append("docx", docxBlob, `Proposal_${proposal.title}.docx`);
      formData.append("documentType", "Proposal");
      formData.append("identifier", proposal.title);

      const token = localStorage.getItem("token");
      const pdfResponse = await axios.post(
        `${API}/upload/convert-to-pdf`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        },
      );

      if (
        pdfResponse.status === 200 &&
        pdfResponse.headers["content-type"] === "application/pdf"
      ) {
        let finalPdfBlob = pdfResponse.data;

        if (appendices.length > 0) {
          const proposalPdf = await PDFDocument.load(
            await pdfResponse.data.arrayBuffer(),
          );
          const mergedPdf = await PDFDocument.create();

          const proposalPages = await mergedPdf.copyPages(
            proposalPdf,
            proposalPdf.getPageIndices(),
          );
          proposalPages.forEach((page) => mergedPdf.addPage(page));

          for (const appendix of appendices) {
            try {
              const appendixResponse = await fetch(appendix.url);
              if (!appendixResponse.ok) {
                throw new Error(
                  `Failed to fetch appendix ${appendix.title}: ${appendixResponse.statusText}`,
                );
              }
              const appendixArrayBuffer = await appendixResponse.arrayBuffer();
              const appendixPdf = await PDFDocument.load(appendixArrayBuffer);
              const appendixPages = await mergedPdf.copyPages(
                appendixPdf,
                appendixPdf.getPageIndices(),
              );
              appendixPages.forEach((page) => mergedPdf.addPage(page));
            } catch (err) {
              console.error(
                `Error processing appendix ${appendix.title}:`,
                err,
              );
              setAlert({
                open: true,
                type: "warning",
                title: "Warning",
                message: `Failed to include appendix ${appendix.title} in the PDF: ${err.message}`,
                onConfirm: () => setAlert({ ...alert, open: false }),
              });
              continue;
            }
          }

          finalPdfBlob = await mergedPdf.save();
        }

        const pdfBlob = new Blob([finalPdfBlob], { type: "application/pdf" });

        try {
          const currentOpportunity = opportunities.find(
            (opp) =>
              (opp._id || opp) ===
              (proposal.opportunity?._id || proposal.opportunity),
          );

          const updateData = {
            estimatedRevenue: subtotal,
            estimationStatus: "completed",
          };

          if (!currentOpportunity.estimationCompletionDate) {
            updateData.estimationCompletionDate = new Date().toISOString();
          }

          await axios.put(`${API}/opportunities/${opportunityId}`, updateData, {
            headers: { Authorization: `Bearer ${token}` },
          });

          setOpportunities((prev) =>
            prev.map((opp) =>
              opp._id === opportunityId
                ? {
                    ...opp,
                    estimatedRevenue: subtotal,
                    estimationStatus: "completed",
                    estimationCompletionDate:
                      updateData.estimationCompletionDate ||
                      opp.estimationCompletionDate,
                  }
                : opp,
            ),
          );
        } catch (err) {
          console.error("Error updating opportunity:", err);
          setAlert({
            open: true,
            type: "error",
            title: "Error",
            message:
              "Failed to update opportunity details: " +
              (err.response?.data?.message || err.message),
            onConfirm: () => setAlert({ ...alert, open: false }),
          });
        }

        if (sendEmail) {
          setPdfBuffer(pdfBlob);
          setCurrentProposal(proposal);
          setShowEmailForm(true);
        } else {
          saveAs(pdfBlob, `Toronto Tapers Proposal - ${proposal.title}`);
          setAlert({
            open: true,
            type: "success",
            title: "Success",
            message:
              "Proposal document with appendices generated successfully!",
            onConfirm: () => setAlert({ ...alert, open: false }),
          });
        }
      } else {
        const textResponse = await pdfResponse.data.text();
        throw new Error(
          JSON.parse(textResponse).message || "Unknown error from server",
        );
      }
    } catch (err) {
      console.error("Error generating document:", err);
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to generate or convert document: " + err.message,
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
    }
  };

  const handleRowClick = (proposalId, event) => {
    // Prevent navigation if the click originated from the Actions column or its children
    if (event.target.closest(".actions-column")) {
      return;
    }
    setIsEditingProposal(proposalId);
  };

  return (
    <div className="bg-gray-800 shadow-xl rounded-xl overflow-hidden mt-4">
      {proposals.length === 0 ? (
        <p className="text-gray-300 text-center py-4">
          No proposals found for this opportunity.
        </p>
      ) : (
        <table className="min-w-full divide-y divide-gray-500">
          <thead className="bg-gray-600">
            <tr>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-200">
                Title
              </th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-200">
                Total Cost
              </th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-200">
                Total Price
              </th>
              <th className="px-6 py-4 text-right text-sm font-semibold text-gray-200 actions-column">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {proposals.map((proposal, index) => (
              <tr
                key={proposal._id}
                className={classNames(
                  index % 2 === 0 ? "bg-gray-800" : "bg-gray-600",
                  "hover:bg-gray-600 transition-colors cursor-pointer",
                )}
                onClick={(e) => handleRowClick(proposal._id, e)}
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                  <span className="inline-flex items-center rounded-full bg-gray-200 px-4 py-0.5 text-sm font-medium text-black hover:bg-gray-300">
                    {proposal.title}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                  $
                  {proposal.totalCost.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                  $
                  {proposal.totalPrice.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right flex space-x-2 justify-end actions-column">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsEditingProposal(proposal._id);
                    }}
                    className="inline-flex items-center rounded-full bg-orange-400 px-4 py-0.5 text-md font-medium text-white hover:bg-orange-500"
                  >
                    Edit
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      generateDocx(proposal, false);
                    }}
                    className="inline-flex items-center rounded-full bg-teal-100 px-4 py-0.5 text-md font-medium text-gray-900 hover:bg-teal-200"
                  >
                    Export
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      generateDocx(proposal, true);
                    }}
                    className="inline-flex items-center rounded-full bg-green-100 px-4 py-0.5 text-md font-medium text-gray-900 hover:bg-green-200"
                  >
                    Send Email
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAwardProposal(proposal);
                    }}
                    className="inline-flex items-center rounded-full bg-blue-100 px-4 py-0.5 text-md font-medium text-gray-900 hover:bg-blue-200"
                  >
                    Awarded
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteProposal(proposal._id);
                    }}
                    className="inline-flex items-center rounded-full bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-700 hover:bg-red-200"
                  >
                    <TrashIcon className="size-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {showEmailForm && (
        <EmailForm
          proposal={currentProposal}
          setShowEmailForm={setShowEmailForm}
          pdfBuffer={pdfBuffer}
          setPdfBuffer={setPdfBuffer}
          setCurrentProposal={setCurrentProposal}
          userEmail={userEmail}
          companyProfile={companyProfile}
          opportunity={opportunities.find(
            (opp) =>
              (opp._id || opp) ===
              (currentProposal.opportunity?._id || currentProposal.opportunity),
          )}
          accounts={accounts}
          contacts={contacts}
        />
      )}
      {showAwardModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-gray-800 p-6 rounded-lg shadow-lg max-w-lg w-full">
            <h3 className="text-lg font-semibold text-orange-400 mb-4">
              Award Opportunity
            </h3>
            <form onSubmit={handleAwardSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-400">
                  Select Proposal
                </label>
                <select
                  name="proposalId"
                  value={awardFormData.proposalId}
                  onChange={(e) =>
                    setAwardFormData({
                      ...awardFormData,
                      proposalId: e.target.value,
                    })
                  }
                  className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                  required
                >
                  <option value="">Select a proposal</option>
                  {proposals.map((proposal) => (
                    <option key={proposal._id} value={proposal._id}>
                      {proposal.title}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-400">
                  Project Manager
                </label>
                <select
                  name="projectManager"
                  value={awardFormData.projectManager}
                  onChange={(e) =>
                    setAwardFormData({
                      ...awardFormData,
                      projectManager: e.target.value,
                    })
                  }
                  className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                >
                  <option value="">Select a Project Manager (Optional)</option>
                  {users
                    .filter((user) =>
                      ["admin", "project_manager"].includes(user.role),
                    )
                    .map((user) => (
                      <option key={user._id} value={user._id}>
                        {user.displayName}
                      </option>
                    ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-400">
                  Foreman
                </label>
                <select
                  name="foreman"
                  value={awardFormData.foreman}
                  onChange={(e) =>
                    setAwardFormData({
                      ...awardFormData,
                      foreman: e.target.value,
                    })
                  }
                  className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                >
                  <option value="">Select a Foreman (Optional)</option>
                  {users
                    .filter((user) =>
                      ["foreman", "manager"].includes(user.role),
                    )
                    .map((user) => (
                      <option key={user._id} value={user._id}>
                        {user.displayName}
                      </option>
                    ))}
                </select>
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={handleAwardCancel}
                  className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-orange-400 text-white px-4 py-2 rounded-md hover:bg-orange-500"
                >
                  Award and Create Project
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <AlertDialog
        open={alert.open}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        onClose={() => setAlert({ ...alert, open: false })}
        onConfirm={alert.onConfirm}
        showCancel={alert.showCancel}
      />
    </div>
  );
}

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}
